import type { PortalCode } from '@/types/identity';

export interface PortalNavItem {
  id: string;
  label: string;
  path: string;
  permission?: string;
  icon?: string;
}

export interface PortalBranding {
  title: string;
  subtitle: string;
  accentClass: string;
  homePath: string;
}

export const PORTAL_BRANDING: Record<PortalCode, PortalBranding> = {
  EMPLOYEE: {
    title: 'Employee Portal',
    subtitle: 'Your workplace hub',
    accentClass: 'from-blue-600 to-indigo-600',
    homePath: '/portals/employee',
  },
  CUSTOMER: {
    title: 'Customer Portal',
    subtitle: 'Orders, invoices & support',
    accentClass: 'from-emerald-600 to-teal-600',
    homePath: '/portals/customer',
  },
  VENDOR: {
    title: 'Vendor Portal',
    subtitle: 'POs, RFQs & payments',
    accentClass: 'from-amber-600 to-orange-600',
    homePath: '/portals/vendor',
  },
  CANDIDATE: {
    title: 'Candidate Portal',
    subtitle: 'Applications & onboarding',
    accentClass: 'from-violet-600 to-purple-600',
    homePath: '/portals/candidate',
  },
  DELIVERY: {
    title: 'Delivery Portal',
    subtitle: 'Routes & proof of delivery',
    accentClass: 'from-cyan-600 to-sky-600',
    homePath: '/portals/delivery',
  },
  PARTNER: {
    title: 'Partner Portal',
    subtitle: 'Collaboration & documents',
    accentClass: 'from-rose-600 to-pink-600',
    homePath: '/portals/partner',
  },
};

export const PORTAL_NAV: Record<PortalCode, PortalNavItem[]> = {
  CUSTOMER: [
    { id: 'dashboard', label: 'Dashboard', path: '/portals/customer' },
    { id: 'profile', label: 'Profile', path: '/portals/customer/profile', permission: 'PORTAL_CUSTOMER_PROFILE' },
    { id: 'orders', label: 'Orders', path: '/portals/customer/orders', permission: 'PORTAL_CUSTOMER_ORDERS' },
    { id: 'invoices', label: 'Invoices', path: '/portals/customer/invoices', permission: 'PORTAL_CUSTOMER_INVOICES' },
    { id: 'payments', label: 'Payments', path: '/portals/customer/payments', permission: 'PORTAL_CUSTOMER_PAYMENTS' },
    { id: 'tickets', label: 'Support', path: '/portals/customer/tickets', permission: 'PORTAL_CUSTOMER_TICKETS' },
    { id: 'documents', label: 'Documents', path: '/portals/customer/documents', permission: 'PORTAL_CUSTOMER_DOCUMENTS' },
    { id: 'subscriptions', label: 'Subscriptions', path: '/portals/customer/subscriptions', permission: 'PORTAL_CUSTOMER_SUBSCRIPTIONS' },
    { id: 'notifications', label: 'Notifications', path: '/portals/customer/notifications', permission: 'PORTAL_CUSTOMER_NOTIFICATIONS' },
  ],
  VENDOR: [
    { id: 'dashboard', label: 'Dashboard', path: '/portals/vendor' },
    { id: 'pos', label: 'Purchase Orders', path: '/portals/vendor/pos', permission: 'PORTAL_VENDOR_POS' },
    { id: 'rfqs', label: 'RFQs', path: '/portals/vendor/rfqs', permission: 'PORTAL_VENDOR_RFQS' },
    { id: 'quotations', label: 'Quotations', path: '/portals/vendor/quotations', permission: 'PORTAL_VENDOR_QUOTATIONS' },
    { id: 'bills', label: 'Vendor Bills', path: '/portals/vendor/bills', permission: 'PORTAL_VENDOR_BILLS' },
    { id: 'payments', label: 'Payments', path: '/portals/vendor/payments', permission: 'PORTAL_VENDOR_PAYMENTS' },
    { id: 'documents', label: 'Documents', path: '/portals/vendor/documents', permission: 'PORTAL_VENDOR_DOCUMENTS' },
    { id: 'performance', label: 'Performance', path: '/portals/vendor/performance', permission: 'PORTAL_VENDOR_PERFORMANCE' },
  ],
  EMPLOYEE: [
    { id: 'dashboard', label: 'Dashboard', path: '/portals/employee' },
    { id: 'attendance', label: 'Attendance', path: '/portals/employee/attendance', permission: 'PORTAL_EMPLOYEE_ATTENDANCE' },
    { id: 'leave', label: 'Leave', path: '/portals/employee/leave', permission: 'PORTAL_EMPLOYEE_LEAVE' },
    { id: 'claims', label: 'Claims', path: '/portals/employee/claims', permission: 'PORTAL_EMPLOYEE_CLAIMS' },
    { id: 'payroll', label: 'Payroll', path: '/portals/employee/payroll', permission: 'PORTAL_EMPLOYEE_PAYROLL' },
    { id: 'documents', label: 'Documents', path: '/portals/employee/documents', permission: 'PORTAL_EMPLOYEE_DOCUMENTS' },
    { id: 'training', label: 'Training', path: '/portals/employee/training', permission: 'PORTAL_EMPLOYEE_TRAINING' },
    { id: 'performance', label: 'Performance', path: '/portals/employee/performance', permission: 'PORTAL_EMPLOYEE_PERFORMANCE' },
    { id: 'approvals', label: 'Approvals', path: '/portals/employee/approvals', permission: 'PORTAL_EMPLOYEE_APPROVALS' },
  ],
  CANDIDATE: [
    { id: 'dashboard', label: 'Dashboard', path: '/portals/candidate' },
    { id: 'applications', label: 'Applications', path: '/portals/candidate/applications', permission: 'PORTAL_CANDIDATE_APPLICATIONS' },
    { id: 'interviews', label: 'Interviews', path: '/portals/candidate/interviews', permission: 'PORTAL_CANDIDATE_INTERVIEWS' },
    { id: 'offers', label: 'Offer Letter', path: '/portals/candidate/offers', permission: 'PORTAL_CANDIDATE_OFFERS' },
    { id: 'documents', label: 'Documents', path: '/portals/candidate/documents', permission: 'PORTAL_CANDIDATE_DOCUMENTS' },
    { id: 'onboarding', label: 'Onboarding', path: '/portals/candidate/onboarding', permission: 'PORTAL_CANDIDATE_ONBOARDING' },
    { id: 'status', label: 'Status', path: '/portals/candidate/status', permission: 'PORTAL_CANDIDATE_STATUS' },
  ],
  DELIVERY: [
    { id: 'dashboard', label: 'Dashboard', path: '/portals/delivery' },
    { id: 'assigned', label: 'Deliveries', path: '/portals/delivery/assigned', permission: 'PORTAL_DELIVERY_ASSIGNED' },
    { id: 'route', label: 'Route', path: '/portals/delivery/route', permission: 'PORTAL_DELIVERY_ROUTE' },
    { id: 'pod', label: 'Proof of Delivery', path: '/portals/delivery/pod', permission: 'PORTAL_DELIVERY_POD' },
    { id: 'payments', label: 'Payments', path: '/portals/delivery/payments', permission: 'PORTAL_DELIVERY_PAYMENTS' },
    { id: 'performance', label: 'Performance', path: '/portals/delivery/performance', permission: 'PORTAL_DELIVERY_PERFORMANCE' },
    { id: 'documents', label: 'Documents', path: '/portals/delivery/documents', permission: 'PORTAL_DELIVERY_DOCUMENTS' },
  ],
  PARTNER: [
    { id: 'dashboard', label: 'Dashboard', path: '/portals/partner' },
    { id: 'documents', label: 'Documents', path: '/portals/partner/documents', permission: 'PORTAL_PARTNER_DOCUMENTS' },
  ],
};

export function portalCodeToPath(code: PortalCode): string {
  return PORTAL_BRANDING[code].homePath;
}
